import { SELL_REFUND, SURFACE_LOCK_COST, TAU } from '../constants';
import { angleDelta, angleDist, norm } from '../polar';
import { coreMaxFor, pushEvent, slotBuildLayer, slotTheta, upgradeLevel } from '../world';
import type {
  Command,
  Content,
  PlanetUpgradeDef,
  Slot,
  StructureDef,
  World,
} from '../types';
import { fireUltimate } from './ultimate';
import { startWave } from './spawning';
import { collectAt } from './drops';

export function buildCost(def: StructureDef): number {
  return def.tiers[0]?.cost ?? Infinity;
}

export function upgradeCost(def: StructureDef, tier: number): number {
  return def.tiers[tier + 1]?.cost ?? Infinity;
}

/** Top rung of this structure's ladder. */
export function maxTier(def: StructureDef): number {
  return def.tiers.length - 1;
}

export function isMaxed(def: StructureDef, tier: number): boolean {
  return tier >= maxTier(def);
}

/**
 * Feeding one more of a thing into a maxed one of the same thing transforms it.
 * The price is the plain build cost — what the card on the bar already says —
 * because the gate is the ladder you climbed to get here, not the fee.
 */
export function mergeCost(def: StructureDef): number {
  return buildCost(def);
}

/**
 * Between waves, recycling is free: taking a gun down to put a better one up is
 * re-planning, and charging for it just makes people keep a bad build. Mid-wave
 * it's 70% (§7) — pulling a gun off the board while something is inbound should
 * cost something.
 *
 * The refund is a share of what was actually sunk into the mount, tracked on
 * the structure itself. Summing the current definition's tier costs stopped
 * being the same number the moment a merge could swap one definition for
 * another — and the gap between the two is exploitable in both directions.
 */
export function sellValue(spent: number, full = false): number {
  return full ? spent : Math.floor(spent * SELL_REFUND);
}

/**
 * Building is allowed at any time, wave running or not.
 *
 * It was locked to the prep phase for a while, on the theory that committing to
 * a plan and living with it made the wave a purer reading exercise. Playing it
 * proved otherwise: watching a bearing collapse with juice in your pocket and
 * no way to spend it is not tension, it's just being stuck. The prep phase
 * still exists — untimed, with a preview of what's coming — but it's a chance
 * to plan, not a gate.
 */
export function isBuildPhase(world: World): boolean {
  return world.waveState === 'prep';
}

export function canBuild(
  world: World,
  content: Content,
  slot: Slot,
  structureId: string,
): { ok: boolean; reason?: string; def?: StructureDef } {
  if (slot.structure) return { ok: false, reason: 'occupied' };
  const def = content.structures.get(structureId);
  if (!def) return { ok: false, reason: 'unknown' };
  // Merge results are earned, never bought — no bar card, no jewel price, and
  // no way to reach one except by taking its source to the top of the ladder.
  if (def.mergeOnly) return { ok: false, reason: 'merge-only' };
  if (def.layer !== 'both' && def.layer !== slotBuildLayer(slot)) {
    return { ok: false, reason: 'wrong-layer' };
  }
  if (def.exclusiveTo && def.exclusiveTo !== world.planet.id) {
    return { ok: false, reason: 'exclusive' };
  }
  if (!world.unlocked.has(def.id)) return { ok: false, reason: 'locked' };
  // A level may hand the player a deliberately short list.
  if (world.level.structures && !world.level.structures.includes(def.id)) {
    return { ok: false, reason: 'not-here' };
  }
  if (world.juice < buildCost(def)) return { ok: false, reason: 'juice' };
  return { ok: true, def };
}

/**
 * What could be built on this mount, ignoring whether it's affordable right
 * now. Empty means the mount is dormant for this level — the tutorial hands out
 * one surface gun, so its orbital mounts have nothing to offer and shouldn't be
 * advertised as if they did.
 */
export function buildableAt(world: World, content: Content, slot: Slot): StructureDef[] {
  if (slot.structure) return [];
  const out: StructureDef[] = [];
  for (const def of content.structures.values()) {
    const why = canBuild(world, content, slot, def.id).reason;
    if (
      why === 'locked' ||
      why === 'not-here' ||
      why === 'wrong-layer' ||
      why === 'exclusive' ||
      why === 'merge-only'
    ) {
      continue;
    }
    out.push(def);
  }
  return out;
}

export function applyCommands(world: World, content: Content, commands: readonly Command[]): void {
  for (const cmd of commands) {
    switch (cmd.kind) {
      case 'build':
        doBuild(world, content, cmd.slot, cmd.structure);
        break;
      case 'merge':
        doMerge(world, content, cmd.slot, cmd.structure);
        break;
      case 'upgrade':
        doUpgrade(world, content, cmd.slot);
        break;
      case 'sell':
        doSell(world, content, cmd.slot);
        break;
      case 'lock':
        doLock(world, cmd.slot);
        break;
      case 'ultimate':
        fireUltimate(world, content, cmd.bearing);
        break;
      case 'collect':
        collectAt(world, cmd.theta, cmd.r);
        break;
      case 'planet-upgrade':
        doPlanetUpgrade(world, content, cmd.upgrade);
        break;
      case 'start-wave':
        if (canStartWave(world)) startWave(world, content);
        break;
    }
  }
}

/**
 * Whether the wave may be committed.
 *
 * Normally the only condition is that you're between waves — walking in with a
 * bare planet is a legitimate gamble. The opening level is the exception: a
 * first-timer who taps start with nothing mounted learns the rules by losing,
 * so `requireBuild` holds the door until at least one defence is down.
 */
export function canStartWave(world: World): boolean {
  if (world.waveState !== 'prep') return false;
  if (world.level.requireBuild !== true) return true;
  return world.slots.some((s) => s.structure !== null);
}

/* --- Planet upgrades ------------------------------------------------ */

export function planetUpgradeCost(def: PlanetUpgradeDef, level: number): number {
  return def.costs[level] ?? Infinity;
}

export function canPlanetUpgrade(
  world: World,
  def: PlanetUpgradeDef,
): { ok: boolean; reason?: string } {
  if (world.level.upgrades && !world.level.upgrades.includes(def.id)) {
    return { ok: false, reason: 'not-here' };
  }
  const level = upgradeLevel(world, def.id);
  if (level >= def.costs.length) return { ok: false, reason: 'max-level' };
  if (world.juice < planetUpgradeCost(def, level)) return { ok: false, reason: 'juice' };
  return { ok: true };
}

function doPlanetUpgrade(world: World, content: Content, id: string): void {
  const def = content.upgrades.get(id);
  if (!def) return;
  const check = canPlanetUpgrade(world, def);
  if (!check.ok) {
    pushEvent(world, { type: 'denied', id: check.reason ?? 'upgrade' });
    return;
  }

  const level = upgradeLevel(world, def.id);
  const cost = planetUpgradeCost(def, level);
  world.juice -= cost;
  world.stats.juiceSpent += cost;
  world.upgrades[def.id] = level + 1;

  applyPlanetUpgrade(world, def.id);
  pushEvent(world, { type: 'planet-upgrade', id: def.id, amount: cost });
}

/**
 * Some upgrades change derived values that are read live (juice multiplier,
 * ring speed, magnet range) and need nothing here. These two change state that
 * has to be materialised.
 */
function applyPlanetUpgrade(world: World, id: string): void {
  if (id === 'hull') {
    // Extra ceiling arrives as usable Integrity, not as a bar that reads full
    // while the planet is still hurt.
    const before = world.coreMax;
    world.coreMax = coreMaxFor(world);
    world.coreHp = Math.min(world.coreMax, world.coreHp + (world.coreMax - before));
    return;
  }
  if (id === 'crust-mounts') addSlot(world, 'surface');
  if (id === 'orbit-mounts') addSlot(world, 'ring');
}

/**
 * Adds a mount and respaces that layer evenly. Structures move with their slot
 * rather than being rebuilt, so buying a mount never costs you what you own.
 */
function addSlot(world: World, layer: 'surface' | 'ring'): void {
  const existing = world.slots.filter((s) => s.layer === layer);
  world.slots.push({
    id: world.slots.length,
    layer,
    baseTheta: 0,
    structure: null,
    locked: false,
    lockTheta: 0,
  });

  const all = [...existing, world.slots[world.slots.length - 1]!];
  const offset = layer === 'surface' ? TAU / (all.length * 2) : 0;
  all.forEach((slot, i) => {
    slot.baseTheta = (i / all.length) * TAU + offset;
    // A pinned surface slot is pinned to a screen bearing, and respacing moved
    // it, so the pin no longer means what the player chose. Release it.
    if (slot.locked) slot.locked = false;
  });
}

function doBuild(world: World, content: Content, slotId: number, structureId: string): void {
  const slot = world.slots[slotId];
  if (!slot) return;
  const check = canBuild(world, content, slot, structureId);
  if (!check.ok || !check.def) {
    pushEvent(world, { type: 'denied', id: check.reason ?? 'build' });
    return;
  }
  const cost = buildCost(check.def);
  world.juice -= cost;
  world.stats.juiceSpent += cost;
  slot.structure = {
    defId: check.def.id,
    tier: 0,
    cooldown: 0,
    charges: check.def.tiers[0]?.charges ?? 0,
    spin: 0,
    spent: cost,
  };
  pushEvent(world, {
    type: 'build',
    id: check.def.id,
    theta: slotTheta(world, slot),
    amount: cost,
  });
}

function doUpgrade(world: World, content: Content, slotId: number): void {
  const slot = world.slots[slotId];
  const st = slot?.structure;
  if (!slot || !st) return;
  const def = content.structures.get(st.defId);
  if (!def || isMaxed(def, st.tier)) {
    pushEvent(world, { type: 'denied', id: 'max-tier' });
    return;
  }
  const cost = upgradeCost(def, st.tier);
  if (world.juice < cost) {
    pushEvent(world, { type: 'denied', id: 'juice' });
    return;
  }
  world.juice -= cost;
  world.stats.juiceSpent += cost;
  st.spent += cost;
  st.tier += 1;
  st.charges = def.tiers[st.tier]?.charges ?? st.charges;
  pushEvent(world, { type: 'upgrade', id: def.id, theta: slotTheta(world, slot), amount: cost });
}

/**
 * Whether a merge is legal here, and what it would produce.
 *
 * Three conditions, all of them things the player can see: the mount holds a
 * structure of the type being offered, that structure is at the top of its
 * ladder, and its definition names something to become.
 */
export function canMerge(
  world: World,
  content: Content,
  slot: Slot,
  structureId: string,
): { ok: boolean; reason?: string; into?: StructureDef } {
  const st = slot.structure;
  if (!st || st.defId !== structureId) return { ok: false, reason: 'not-a-pair' };
  const def = content.structures.get(st.defId);
  if (!def?.mergesInto) return { ok: false, reason: 'no-merge' };
  if (!isMaxed(def, st.tier)) return { ok: false, reason: 'not-maxed' };
  const into = content.structures.get(def.mergesInto);
  if (!into) return { ok: false, reason: 'unknown' };
  if (world.juice < mergeCost(def)) return { ok: false, reason: 'juice' };
  return { ok: true, into };
}

function doMerge(world: World, content: Content, slotId: number, structureId: string): void {
  const slot = world.slots[slotId];
  if (!slot) return;
  const check = canMerge(world, content, slot, structureId);
  const st = slot.structure;
  if (!check.ok || !check.into || !st) {
    pushEvent(world, { type: 'denied', id: check.reason ?? 'merge' });
    return;
  }
  const def = content.structures.get(st.defId)!;
  const cost = mergeCost(def);
  world.juice -= cost;
  world.stats.juiceSpent += cost;

  st.defId = check.into.id;
  st.tier = 0;
  st.spent += cost;
  st.cooldown = 0;
  st.charges = check.into.tiers[0]?.charges ?? 0;
  pushEvent(world, { type: 'merge', id: check.into.id, theta: slotTheta(world, slot), amount: cost });
}

function doSell(world: World, content: Content, slotId: number): void {
  const slot = world.slots[slotId];
  const st = slot?.structure;
  if (!slot || !st) return;
  const def = content.structures.get(st.defId);
  if (!def) return;
  const refund = sellValue(st.spent, isBuildPhase(world));
  world.juice += refund;
  slot.structure = null;
  slot.locked = false;
  pushEvent(world, { type: 'sell', id: def.id, theta: slotTheta(world, slot), amount: refund });
}

/**
 * Pin a surface slot to its current screen bearing so it stops riding the
 * crust spin. Costs Juice — holding a bearing should be a decision.
 */
function doLock(world: World, slotId: number): void {
  const slot = world.slots[slotId];
  if (!slot || slot.layer !== 'surface') return;
  if (slot.locked) {
    slot.locked = false;
    return;
  }
  if (world.juice < SURFACE_LOCK_COST) {
    pushEvent(world, { type: 'denied', id: 'juice' });
    return;
  }
  world.juice -= SURFACE_LOCK_COST;
  world.stats.juiceSpent += SURFACE_LOCK_COST;
  slot.lockTheta = slotTheta(world, slot);
  slot.locked = true;
}
